import React, { useState, useEffect } from "react";
import {
  MapContainer,
  TileLayer,
  FeatureGroup,
  LayersControl,
  Marker,
  Popup,
  ImageOverlay,
  Rectangle,
} from "react-leaflet";
import MapLegendWrapper from '../../components/MapLegendWrapper';
import { EditControl } from "react-leaflet-draw";
import { useDispatch, useSelector } from "react-redux";
import {
  addArea,
  clearAllMapState,
  deleteArea,
  setFileContent,
  setUploaderFlag,
} from "../../reducers/MapSlice";
import { useMap } from "react-leaflet/hooks";
import L from "leaflet";
import {useLeafletLocale} from "../../hooks/useLeafletLocale"
import markerIconPng from "leaflet/dist/images/marker-icon.png";
import { getWeatherData } from "../../api/WeatherApi";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { Icon } from "leaflet";
import { isMarkerWithinArabBoundries } from "../../utils/conversions";
import testImg from "./download.png";
import { CRS } from "leaflet";
import "./styles.css";
import zIndex from "@mui/material/styles/zIndex";

function Map(props) {
  console.log("LEGEND DATA:", props.legendData)
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const markerRef = React.useRef(null);
  const center = { lat: 19.55979, lng: 29.267578 };
  const [markerPosition, setMarkerPosition] = React.useState(center);
  // const weatherForcastRequest = useApi(getWeatherForcastData)
  // const weatherHistoryRequest = useApi(get)

  const areas = useSelector((state) => state.mapState.areas);
  const datePickerTimeFrom = useSelector(
    (state) => state.mapState.date.datePickerTimeFrom
  );
  const datePickerTimeTo = useSelector(
    (state) => state.mapState.date.datePickerTimeTo
  );
  const currentWeather = useSelector((state) => state.mapState.currentWeather);
  const fileContent = useSelector(
    (state) => state.mapState.uploadedFileContent
  );
  const uploaderFlag = useSelector((state) => state.mapState.uploaderFlag);
  const [deleteState, setDelete] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  useLeafletLocale();


  /**
   * event fired when user adds a polygon to leaflet
   * @param {*} e
   */
  const _onCreated = (e) => {
    // console.log(e);
    let layer = e.layer;
    dispatch(
      addArea({
        polyId: layer._leaflet_id,
        coordinates: layer.toGeoJSON().geometry.coordinates[0],
      })
    );
  };

  /**
   * event fired when user deletes polygon/s to leaflet
   * @param {*} e
   */
  const _onDeleted = (e) => {
    // console.log(e);
    const deletedKeys = Object.keys(e.layers._layers);
    //console.log("deleted keys", deletedKeys);
    dispatch(deleteArea(deletedKeys));
  };

  function DeleteGeoJson() {
    const map = useMap();
    // console.log("here");
    map.eachLayer(function (layer) {
      if (layer instanceof L.Polygon) {
        layer.remove();
      }
    });
    dispatch(setUploaderFlag(false));
    setDelete(false);
    dispatch(clearAllMapState());
  }

  const markerDragHandler = {
    dragend() {
      const marker = markerRef.current;
      if (marker != null) {
        if (!isMarkerWithinArabBoundries(marker)) {
          toast.warn(t("Map.within_arab"));
          return;
        }
        //dates aren't null
        if (!datePickerTimeFrom || !datePickerTimeTo) {
          toast.warn(t("Map.select_dates"));
          return;
        }
        getWeatherData(dispatch, t, datePickerTimeFrom, datePickerTimeTo, {
          lat: markerRef.current.getLatLng().lat.toFixed(2),
          lng: markerRef.current.getLatLng().lng.toFixed(2),
        });
      }
    },
  };

  const [boundForGeoJson, setboundForGeoJson] = useState();

  function AddGeoJson() {
    const map = useMap();

    // Clear existing GeoJSON layers first
    map.eachLayer((layer) => {
      if (layer instanceof L.GeoJSON || layer instanceof L.Polygon) {
        map.removeLayer(layer);
      }
    });

    try {
      // Validate fileContent
      if (!fileContent || typeof fileContent !== 'object') {
        console.error('Invalid fileContent:', fileContent);
        toast.error('Invalid file content');
        return null;
      }

      // Basic GeoJSON validation
      if (!fileContent.type || !Array.isArray(fileContent.features)) {
        console.error('Invalid GeoJSON structure:', fileContent);
        toast.error('Invalid GeoJSON structure');
        return null;
      }

      // Create GeoJSON layer with error handling
      const geoJsonLayer = L.geoJSON(fileContent, {
        style: {
          fillOpacity: areas.some(area => area.img64) ? 0 : 0.2,
          fillColor: '#1976d2',
          weight: 2,
          color: '#1976d2'
        },
        onEachFeature: (feature, layer) => {
          if (feature.properties) {
            layer.bindPopup(
              Object.entries(feature.properties)
                .map(([key, value]) => `${key}: ${value}`)
                .join('<br>')
            );
          }
        }
      });

      if (!geoJsonLayer) {
        throw new Error('Failed to create GeoJSON layer');
      }

      // Add to map and get bounds
      geoJsonLayer.addTo(map);
      const bounds = geoJsonLayer.getBounds();

      if (!bounds.isValid()) {
        throw new Error('Invalid bounds');
      }

      setboundForGeoJson(bounds);
      const boundList = convertToBounds(bounds);

      // Dispatch area and clear content
      dispatch(
        addArea({
          polyId: geoJsonLayer._leaflet_id,
          coordinates: fileContent,
        })
      );
      dispatch(setFileContent(""));

      // Fit map to bounds
      map.fitBounds(bounds);

      return null;
    } catch (error) {
      console.error('Error in AddGeoJson:', error);
      toast.error('Failed to display GeoJSON on map');
      dispatch(setFileContent(""));
      return null;
    }
  }

  function deleteUploadedFiles() {
    localStorage.removeItem('mapFileContent');
    dispatch(clearAllMapState());
    dispatch(setUploaderFlag(false));
    setDelete(true);
  }

  function convertToBounds(obj) {
    const { _southWest, _northEast } = obj;

    const west = _southWest.lng;
    const east = _northEast.lng;
    const north = _northEast.lat;
    const south = _southWest.lat;

    return [
      [west, south],
      [west, north],
      [east, north],
      [east, south],
    ];
  }

  const ArabWorldBounds = {
    southWest: {
      lat: -18.454445427710418,
      lng: -18.675611088484246,
    },
    northEast: {
      lat: 45.261019159784439,
      lng: 60.756334763925977,
    },
  };
  const bounds = L.latLngBounds(
    ArabWorldBounds.southWest,
    ArabWorldBounds.northEast
  );
  const { showEditControl } = props;

  // const areas = useSelector((state) => state.mapState.areas);

  // function getBounds(coordinates) {
  //   const southwest = [coordinates[0][1], coordinates[0][0]]; // [longitude, latitude]
  //   const northeast = [coordinates[2][1], coordinates[2][0]]; // [longitude, latitude]

  //   // Define the bounds of the shape using southwest and northeast corners
  //   return [southwest, northeast];
  // }

  // function DrawMaps() {
  //   const map = useMap();
  //   map.whenReady(() => {
  //     setTimeout(() => {
  //       map.invalidateSize();
  //     }, 0);
  //   });
  //   areas.forEach((area) => {
  //     if (area.img64) {
  //       const coords = area.coordinates.map((c) => [c[1], c[0]]);
  //       L.imageOverlay("data:image/jpeg;base64," + area.img64, coords, {
  //         interactive: true,
  //         opacity: 0.8,
  //       }).addTo(map);
  //     }
  //   });

  //   return null;
  // }

  return (
    <>
      <MapContainer
        bounds={bounds}
        maxBounds={bounds}
        center={center}
        minZoom={4}
      >
        {areas &&
          areas.map(
            (area, index) =>
              (area.imgurl || area.img64) && area.bbox && (
                <ImageOverlay
                  key={index}
                  url={
                    area.imgurl ||
                    (area.img64 && `data:image/png;base64,${area.img64}`)
                  } // Assuming imgurl contains the URL for the image
                  bounds={area.bbox} // Assuming coordinates contains the bounds of the image overlay
                />
              )
          )}

        {/* {fetchFaoImage ? <props.tileFunction /> : null} */}

        {fileContent && <AddGeoJson />}
        {deleteState && <DeleteGeoJson />}
        <TileLayer
          attribution="Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community"
          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
        />

        {props.legendData && <MapLegendWrapper legends={props.legendData} />}

        {!showEditControl && (
          <Marker
            draggable={true}
            eventHandlers={markerDragHandler}
            position={markerPosition}
            ref={markerRef}
            icon={
              new Icon({
                iconUrl: markerIconPng,
                iconSize: [25, 41],
                iconAnchor: [12, 41],
              })
            }
          >
            <Popup>
              <div>
                <p>Percipitation :{currentWeather["precipitation_sum"]}</p>
                <p>Temperature: {currentWeather["temperature_2m_max"]}</p>
                <p>WindSpeed : {currentWeather["windspeed_10m_max"]}</p>
              </div>
            </Popup>
          </Marker>
        )}

        {(showEditControl && props.index) && (
          <FeatureGroup className="edit-control-container">
            <EditControl
              position="topleft"
              onCreated={(e) => {
                setIsDrawing(false);
                _onCreated(e);
              }}
              onDeleted={_onDeleted}
              onDrawStart={(e) => {
                setIsDrawing(true);
                const map = e.target;
                  localStorage.removeItem('mapFileContent');
    dispatch(clearAllMapState());
    dispatch(setUploaderFlag(false));
    setDelete(true);
                // Clear existing shapes but not the one being drawn
                map.eachLayer((layer) => {
                  if (layer instanceof L.Polygon || layer instanceof L.GeoJSON) {
                    map.removeLayer(layer);
                  }
                });
              }}
              onDrawStop={() => {
                setIsDrawing(false);
              }}
              draw={{
                rectangle: true,
                circle: true,
                polyline: false,
                marker: false,
                polygon: true,
                circlemarker: false,
              }}
            />
          </FeatureGroup>
        )}
        {/* <DrawMaps /> */}
      </MapContainer>
      {uploaderFlag ? (
        <div className="remove-area">
        <button className="delete-button" onClick={() => deleteUploadedFiles()}>
          {t("Map.remove_file")}
        </button>
        </div>
      ) : (
        <div></div>
      )}
    </>
  );
}

export default Map;
